import {
  DEFAULT_GRID_AUTOSIZE_OPTIONS, type GridAutosizeOptions,
  type GridColDef, type GridSortModel
} from '@mui/x-data-grid-pro'
import {
  formatUsdPrice, formatRoundedCoinAmount,
  formatRoundedPosition, roundCoinAmount,
  formatRoundedUsdAmount, roundUsdAmount
} from '../../utils/rounding-formatting-utils'
import clsx from 'clsx'
import { type MODE_OPTIONS } from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid-types'

const EXPOSURE_COL_DEF_COIN: GridColDef[] = [
  { field: 'coin', headerName: 'Coin', type: 'string', minWidth: 70 },
  {
    field: 'account',
    headerName: 'Account',
    type: 'string',
    minWidth: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  { field: 'account_type', headerName: 'Account Type', type: 'string', minWidth: 110 },
  { field: 'symbol', headerName: 'Symbol', type: 'string', minWidth: 100 },
  {
    field: 'account_exposure',
    headerName: 'Account Exposure',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatRoundedCoinAmount(value)
  },
  {
    field: 'price_usd',
    headerName: 'Price USD',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatUsdPrice(value)
  },
  {
    field: 'exposure_coin',
    headerName: 'Exposure Coin',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatRoundedCoinAmount(value)
  },
  {
    field: 'exposure_usd',
    headerName: 'Exposure USD',
    type: 'number',
    minWidth: 100,
    valueFormatter: (value) => formatRoundedUsdAmount(value)
  },
  {
    field: 'initial_exposure_coin',
    headerName: 'Initial Exposure',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatRoundedCoinAmount(value)
  },
  {
    field: 'net_coin',
    headerName: 'Net Coin',
    type: 'number',
    minWidth: 60,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedCoinAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundCoinAmount(Number(params.value)) > 0,
        negative: roundCoinAmount(Number(params.value)) < 0
      })
    }
  },
  {
    field: 'net_usd',
    headerName: 'Net USD',
    type: 'number',
    minWidth: 100,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedUsdAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundUsdAmount(Number(params.value)) > 0,
        negative: roundUsdAmount(Number(params.value)) < 0
      })
    }
  },
  {
    field: 'position_value',
    headerName: 'Position Value',
    type: 'number',
    minWidth: 80,
    valueFormatter: (value) => formatRoundedPosition(value),
  }
]

const EXPOSURE_COL_DEF_ACCOUNT: GridColDef[] = [
  {
    field: 'account',
    headerName: 'Account',
    type: 'string',
    minWidth: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  { field: 'account_type', headerName: 'Account Type', type: 'string', minWidth: 110 },
  { field: 'coin', headerName: 'Coin', type: 'string', minWidth: 70 },
  { field: 'symbol', headerName: 'Symbol', type: 'string', minWidth: 100 },
  {
    field: 'account_exposure',
    headerName: 'Account Exposure',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatRoundedCoinAmount(value)
  },
  {
    field: 'price_usd',
    headerName: 'Price USD',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatUsdPrice(value)
  },
  {
    field: 'exposure_coin',
    headerName: 'Exposure Coin',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatRoundedCoinAmount(value)
  },
  {
    field: 'exposure_usd',
    headerName: 'Exposure USD',
    type: 'number',
    minWidth: 100,
    valueFormatter: (value) => formatRoundedUsdAmount(value)
  },
  {
    field: 'initial_exposure_coin',
    headerName: 'Initial Exposure',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatRoundedCoinAmount(value)
  },
  {
    field: 'net_coin',
    headerName: 'Net Coin',
    type: 'number',
    minWidth: 60,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedCoinAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundCoinAmount(Number(params.value)) > 0,
        negative: roundCoinAmount(Number(params.value)) < 0
      })
    }
  },
  {
    field: 'net_usd',
    headerName: 'Net USD',
    type: 'number',
    minWidth: 100,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedUsdAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundUsdAmount(Number(params.value)) > 0,
        negative: roundUsdAmount(Number(params.value)) < 0
      })
    }
  },
  {
    field: 'position_value',
    headerName: 'Position Value',
    type: 'number',
    minWidth: 80,
    valueFormatter: (value) => formatRoundedPosition(value),
  }
]

const EXPOSURE_COL_DEF_SYMBOL: GridColDef[] = [
  {
    field: 'symbol',
    headerName: 'Symbol',
    type: 'string',
    minWidth: 120,
    sortComparator: (v1: string, v2: string) => {
      const v1Balance = v1.includes('BALANCE')
      const v2Balance = v2.includes('BALANCE')
      if (v1Balance && v2Balance) {
        return v1.localeCompare(v2, undefined, { numeric: true })
      } else if (v1Balance) {
        return 1
      } else if (v2Balance) {
        return -1
      } else {
        return v1.localeCompare(v2, undefined, { numeric: true })
      }
    },
  },
  { field: 'coin', headerName: 'Coin', type: 'string', minWidth: 70 },
  {
    field: 'account',
    headerName: 'Account',
    type: 'string',
    minWidth: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  {
    field: 'account_exposure',
    headerName: 'Account Exposure',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatRoundedCoinAmount(value)
  },
  {
    field: 'price_usd',
    headerName: 'Price USD',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatUsdPrice(value)
  },
  {
    field: 'exposure_coin',
    headerName: 'Exposure Coin',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatRoundedCoinAmount(value)
  },
  {
    field: 'exposure_usd',
    headerName: 'Exposure USD',
    type: 'number',
    minWidth: 100,
    valueFormatter: (value) => formatRoundedUsdAmount(value)
  },
  {
    field: 'initial_exposure_coin',
    headerName: 'Initial Exposure',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatRoundedCoinAmount(value)
  },
  {
    field: 'net_coin',
    headerName: 'Net Coin',
    type: 'number',
    minWidth: 60,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedCoinAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundCoinAmount(Number(params.value)) > 0,
        negative: roundCoinAmount(Number(params.value)) < 0
      })
    }
  },
  {
    field: 'net_usd',
    headerName: 'Net USD',
    type: 'number',
    minWidth: 100,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedUsdAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundUsdAmount(Number(params.value)) > 0,
        negative: roundUsdAmount(Number(params.value)) < 0
      })
    }
  },
  {
    field: 'position_value',
    headerName: 'Position Value',
    type: 'number',
    minWidth: 80,
    valueFormatter: (value) => formatRoundedPosition(value),
  }
]

const EXPOSURE_GROUPING_COIN_SORTING: GridSortModel = [
  { field: 'coin', sort: 'asc' },
  { field: 'account', sort: 'asc' },
  { field: 'account_type', sort: 'asc' }]

const EXPOSURE_GROUPING_ACCOUNT_SORTING: GridSortModel = [
  { field: 'account', sort: 'asc' },
  { field: 'account_type', sort: 'asc' },
  { field: 'coin', sort: 'asc' }
]
const EXPOSURE_GROUPING_SYMBOL_SORTING: GridSortModel = [
  { field: 'symbol', sort: 'asc' },
  { field: 'coin', sort: 'asc' },
  { field: 'account', sort: 'asc' },
  { field: 'account_type', sort: 'asc' }
]

export interface ExposureModeOptionsConfig {
  endpoint: string | undefined
  columns: GridColDef[]
  autosizeOptions: GridAutosizeOptions
  sortModel: GridSortModel
}

export const EXPOSURE_MODE_OPTIONS_CONFIG: Record<MODE_OPTIONS, ExposureModeOptionsConfig> = {
  none: {
    endpoint: undefined,
    columns: [],
    autosizeOptions: DEFAULT_GRID_AUTOSIZE_OPTIONS,
    sortModel: [],
  },
  coin: {
    endpoint: process.env.REACT_APP_API_EXPOSURE,
    columns: EXPOSURE_COL_DEF_COIN,
    autosizeOptions: {
      columns: Array.from(EXPOSURE_COL_DEF_COIN, item => item?.field),
      includeHeaders: true,
      includeOutliers: true
    },
    sortModel: EXPOSURE_GROUPING_COIN_SORTING,
  },
  account: {
    endpoint: process.env.REACT_APP_API_EXPOSURE_ACCOUNT,
    columns: EXPOSURE_COL_DEF_ACCOUNT,
    autosizeOptions: {
      columns: Array.from(EXPOSURE_COL_DEF_ACCOUNT, item => item?.field),
      includeHeaders: true,
      includeOutliers: true
    },
    sortModel: EXPOSURE_GROUPING_ACCOUNT_SORTING,
  },
  symbol: {
    endpoint: process.env.REACT_APP_API_EXPOSURE_SYMBOL,
    columns: EXPOSURE_COL_DEF_SYMBOL,
    autosizeOptions: {
      columns: Array.from(EXPOSURE_COL_DEF_SYMBOL, item => item?.field),
      includeHeaders: true,
      includeOutliers: true
    },
    sortModel: EXPOSURE_GROUPING_SYMBOL_SORTING,
  }
}
